import React, { useEffect, useState, useMemo } from 'react';
import '../styles/EventCalendar.css';
import DateInput from './DateInput';

/**
 * EventCalendar
 * ----------------------------------------------------------------------------
 * Two-mode component for the lightweight event calendar feature.
 *
 *  - mode="manager": ky_thuat / ban_dieu_hanh / ban_dao_tao / ban_su_kien.
 *      Lets them toggle the feature on/off, create / edit / delete events,
 *      and inspect them grouped into Sắp Diễn Ra / Đang Diễn Ra / Hết Hạn.
 *  - mode="member" (mam_non / general member): read-only list of upcoming
 *      and ongoing events. Expired ones are filtered out by the server.
 *      Click an item to expand details inline.
 */
const WEEKDAY_VN = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
const fmt = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}, ${WEEKDAY_VN[d.getDay()]}, ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  } catch (_) { return ''; }
};

// Two-line variant for chips: top = HH:MM, bottom = Thứ X, DD/MM/YYYY.
const fmtParts = (iso) => {
  if (!iso) return { time: '', date: '' };
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { time: '', date: '' };
    const pad = (n) => String(n).padStart(2, '0');
    return {
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      date: `${WEEKDAY_VN[d.getDay()]}, ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
    };
  } catch (_) { return { time: '', date: '' }; }
};

// HTML <input type="datetime-local"> needs YYYY-MM-DDTHH:MM in the LOCAL
// timezone. Doing this manually avoids the UTC-shift bug that
// `toISOString().slice(0,16)` would introduce.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const classifyEvent = (ev, now = Date.now()) => {
  const start = ev.startAt ? new Date(ev.startAt).getTime() : 0;
  const end = ev.endAt ? new Date(ev.endAt).getTime() : start;
  if (now < start) return 'upcoming';
  if (now > end) return 'expired';
  return 'ongoing';
};

const emptyForm = { id: null, title: '', description: '', location: '', startAt: '', endAt: '', notifyBans: [], notifyPositions: [] };

// Custom date+time picker matching the style of the main event-manager form
// (DateInput for the date — dd/mm/yyyy plain-text with validation — plus a
// short HH:MM time field). Values flow up as `YYYY-MM-DDTHH:MM` so the rest
// of the form / server logic stays untouched.
const splitLocal = (v) => {
  if (!v) return { date: '', time: '' };
  const [d, t] = String(v).split('T');
  return { date: d || '', time: (t || '').slice(0, 5) };
};
const joinLocal = (date, time) => (date ? `${date}T${time || '00:00'}` : '');

const normalizeTime = (raw) => {
  if (!raw) return '';
  const cleaned = String(raw).trim().replace(/[^\d:]/g, '');
  const m = cleaned.match(/^(\d{1,2}):?(\d{0,2})$/);
  if (!m) return '';
  const h = Math.min(23, parseInt(m[1] || '0', 10));
  const mi = Math.min(59, parseInt(m[2] || '0', 10));
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
};

const DateTimeField = ({ label, value, onChange, required }) => {
  const { date, time } = splitLocal(value);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span>{label}</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <DateInput
          value={date}
          required={required}
          onChange={(iso) => onChange(joinLocal(iso, time))}
          placeholder="dd/mm/yyyy"
          style={{ flex: '1 1 160px', minWidth: 0 }}
        />
        <input
          type="text"
          inputMode="numeric"
          value={time}
          required={required}
          placeholder="hh:mm"
          maxLength={5}
          onChange={(e) => {
            let v = e.target.value.replace(/[^\d:]/g, '').slice(0, 5);
            // Auto-insert ':' after 2 digits if user typed only numbers.
            if (/^\d{3}$/.test(v)) v = v.slice(0, 2) + ':' + v.slice(2);
            onChange(joinLocal(date, v));
          }}
          onBlur={(e) => {
            const normalized = normalizeTime(e.target.value);
            if (normalized !== time) onChange(joinLocal(date, normalized));
          }}
          style={{ flex: '0 1 110px', minWidth: 0 }}
          title="Giờ (HH:MM, 24h)"
        />
      </div>
      {date && time && (
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)' }}>
          {fmt(`${date}T${time}:00`)}
        </span>
      )}
    </label>
  );
};

const BAN_OPTS = [
  { key: 'ban_dieu_hanh', label: 'Ban Điều Hành' },
  { key: 'ban_dao_tao', label: 'Ban Đào Tạo' },
  { key: 'ban_su_kien', label: 'Ban Sự Kiện' },
  { key: 'ky_thuat', label: 'Kỹ Thuật' }
];
const POSITION_OPTS = [
  { key: 'mam_non', label: 'Mầm Non' },
  { key: 'tap_su', label: 'Tập Sự' },
  { key: 'culi_cap_cao', label: 'Culi Cấp Cao' },
  { key: 'bo_lao', label: 'Bô Lão' }
];

const EventCalendar = ({ token, mode = 'member' }) => {
  const [enabled, setEnabled] = useState(true);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [view, setView] = useState('upcoming'); // manager: upcoming | ongoing | expired
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const auth = token ? { Authorization: `Bearer ${token}` } : {};
  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const load = () => {
    fetch('/api/event-calendar', { headers: auth })
      .then(r => r.json())
      .then(d => {
        if (!d) return;
        if (typeof d.enabled === 'boolean') setEnabled(d.enabled);
        setEvents(Array.isArray(d.events) ? d.events : []);
      })
      .catch(err => show('error', 'Không tải được lịch: ' + err.message));
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const now = Date.now();
    const out = { upcoming: [], ongoing: [], expired: [] };
    events.forEach(ev => { out[classifyEvent(ev, now)].push(ev); });
    Object.keys(out).forEach(k => out[k].sort((a, b) => new Date(a.startAt) - new Date(b.startAt)));
    return out;
  }, [events]);

  // Member view: combined (upcoming + ongoing), sorted by startAt asc.
  const memberList = useMemo(() => {
    return [...grouped.ongoing, ...grouped.upcoming].sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
  }, [grouped]);

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/event-calendar/enabled', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ enabled: !enabled })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lỗi.');
      setEnabled(data.enabled);
      show('success', data.enabled ? 'Đã bật chức năng lịch sự kiện CLB.' : 'Đã tắt chức năng lịch sự kiện CLB.');
    } catch (err) { show('error', err.message); } finally { setBusy(false); }
  };

  const startEdit = (ev) => {
    setForm({
      id: ev.id,
      title: ev.title || '',
      description: ev.description || '',
      location: ev.location || '',
      startAt: toLocalInput(ev.startAt),
      endAt: toLocalInput(ev.endAt),
      notifyBans: Array.isArray(ev.notifyBans) ? ev.notifyBans : [],
      notifyPositions: Array.isArray(ev.notifyPositions) ? ev.notifyPositions : []
    });
    setShowForm(true);
  };
  const cancelForm = () => { setForm(emptyForm); setShowForm(false); };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return show('error', 'Thiếu tiêu đề.');
    if (!form.startAt) return show('error', 'Thiếu thời gian bắt đầu.');
    setBusy(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        location: form.location,
        startAt: new Date(form.startAt).toISOString(),
        endAt: form.endAt ? new Date(form.endAt).toISOString() : new Date(form.startAt).toISOString(),
        notifyBans: form.notifyBans,
        notifyPositions: form.notifyPositions
      };
      const url = form.id ? `/api/event-calendar/${form.id}` : '/api/event-calendar';
      const method = form.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lỗi.');
      show('success', form.id ? 'Đã cập nhật lịch.' : 'Đã tạo lịch.');
      cancelForm();
      load();
    } catch (err) { show('error', err.message); } finally { setBusy(false); }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Xoá lịch "${ev.title}"?`)) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/event-calendar/${ev.id}`, { method: 'DELETE', headers: auth });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Xoá thất bại.');
      show('success', 'Đã xoá lịch.');
      load();
    } catch (err) { show('error', err.message); } finally { setBusy(false); }
  };

  // ---------- Member view ----------
  if (mode === 'member') {
    if (!enabled) {
      return (
        <div className="dashboard-message">
          Chức năng lịch sự kiện CLB hiện đang tạm dừng. Vui lòng quay lại sau.
        </div>
      );
    }
    return (
      <div className="event-calendar-member">
        <h2 style={{ marginTop: 0 }}>Lịch Sự Kiện CLB</h2>
        {memberList.length === 0 && (
          <p className="form-hint">Hiện chưa có sự kiện nào sắp diễn ra.</p>
        )}
        <ul className="event-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {memberList.map(ev => {
            const status = classifyEvent(ev);
            const isOpen = expanded === ev.id;
            return (
              <li
                key={ev.id}
                className={`event-card event-${status}`}
                style={{
                  border: '1px solid var(--border-primary)',
                  borderLeft: `4px solid ${status === 'ongoing' ? 'var(--color-success, #2bb673)' : 'var(--accent-primary, #4f8eff)'}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  background: 'var(--bg-secondary)',
                  cursor: 'pointer'
                }}
                onClick={() => setExpanded(isOpen ? null : ev.id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <strong>{ev.title}</strong>
                  {(() => {
                    const p = fmtParts(ev.startAt);
                    return (
                      <span className={`event-time-chip ${status === 'ongoing' ? 'is-ongoing' : ''}`}>
                        <span className="etc-label">{status === 'ongoing' ? '● Đang diễn ra' : '○ Sắp diễn ra'}</span>
                        <span className="etc-time">{p.time}</span>
                        <span className="etc-date">{p.date}</span>
                      </span>
                    );
                  })()}
                </div>
                {isOpen && (
                  <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.55 }}>
                    {ev.location && <div>📍 <strong>Địa điểm:</strong> {ev.location}</div>}
                    <div>🕐 <strong>Bắt đầu:</strong> {fmt(ev.startAt)}</div>
                    {ev.endAt && ev.endAt !== ev.startAt && <div>🕑 <strong>Kết thúc:</strong> {fmt(ev.endAt)}</div>}
                    {ev.description && (
                      <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{ev.description}</div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // ---------- Manager view ----------
  const list = grouped[view] || [];
  return (
    <div className="event-calendar-admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>Quản Lý Lịch Sự Kiện CLB</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={enabled} onChange={toggleEnabled} disabled={busy} />
            <span>Hiển thị cho thành viên</span>
          </label>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setForm(emptyForm); setShowForm(true); }}>
            + Tạo lịch mới
          </button>
        </div>
      </div>

      {msg && (
        <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'}`} style={{ marginBottom: 12 }}>
          {msg.text}
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} className="admin-form-card" style={{ marginBottom: 16, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h4 style={{ margin: 0 }}>{form.id ? 'Sửa lịch' : 'Tạo lịch mới'}</h4>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>Tiêu đề *</span>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required maxLength={200} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>Địa điểm</span>
            <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} maxLength={300} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <DateTimeField
              label="Bắt đầu *"
              required
              value={form.startAt}
              onChange={(v) => setForm({ ...form, startAt: v })}
            />
            <DateTimeField
              label="Kết thúc"
              value={form.endAt}
              onChange={(v) => setForm({ ...form, endAt: v })}
            />
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>Nội dung sự kiện</span>
            <textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} maxLength={5000} />
          </label>
          <fieldset style={{ border: '1px solid var(--border-primary)', borderRadius: 6, padding: '8px 12px' }}>
            <legend style={{ padding: '0 6px', fontSize: 13, color: 'var(--text-secondary)' }}>Đối tượng hiển thị</legend>
            <p className="form-hint" style={{ margin: '0 0 8px', fontSize: 12 }}>
              Chọn ban và/hoặc cấp thành viên sẽ nhìn thấy lịch này. Không chọn gì → hiển thị cho tất cả thành viên.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Theo ban</div>
                {BAN_OPTS.map(opt => (
                  <label key={opt.key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, padding: '2px 0' }}>
                    <input
                      type="checkbox"
                      checked={form.notifyBans.includes(opt.key)}
                      onChange={e => setForm(f => ({
                        ...f,
                        notifyBans: e.target.checked ? [...f.notifyBans, opt.key] : f.notifyBans.filter(x => x !== opt.key)
                      }))}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Theo cấp thành viên</div>
                {POSITION_OPTS.map(opt => (
                  <label key={opt.key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, padding: '2px 0' }}>
                    <input
                      type="checkbox"
                      checked={form.notifyPositions.includes(opt.key)}
                      onChange={e => setForm(f => ({
                        ...f,
                        notifyPositions: e.target.checked ? [...f.notifyPositions, opt.key] : f.notifyPositions.filter(x => x !== opt.key)
                      }))}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          </fieldset>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{form.id ? 'Cập nhật' : 'Tạo'}</button>
            <button type="button" className="btn btn-outline btn-sm" onClick={cancelForm}>Huỷ</button>
          </div>
        </form>
      )}

      {!showForm && (
        <>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { key: 'upcoming', label: `Sắp diễn ra (${grouped.upcoming.length})` },
            { key: 'ongoing', label: `Đang diễn ra (${grouped.ongoing.length})` },
            { key: 'expired', label: `Hết hạn (${grouped.expired.length})` }
          ].map(t => (
            <button
              key={t.key}
              type="button"
              className={`btn btn-sm ${view === t.key ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setView(t.key)}
            >{t.label}</button>
          ))}
        </div>

      {list.length === 0 ? (
        <p className="form-hint">Không có lịch nào trong nhóm này.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map(ev => (
            <li key={ev.id} style={{
              border: '1px solid var(--border-primary)',
              borderRadius: 8,
              padding: '10px 12px',
              background: 'var(--bg-secondary)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <strong>{ev.title}</strong>
                  <div style={{ margin: '8px 0 0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(() => { const p = fmtParts(ev.startAt); return (
                      <span className="event-time-chip">
                        <span className="etc-label">Bắt đầu</span>
                        <span className="etc-time">{p.time}</span>
                        <span className="etc-date">{p.date}</span>
                      </span>
                    ); })()}
                    {ev.endAt && ev.endAt !== ev.startAt && (() => { const p = fmtParts(ev.endAt); return (
                      <span className="event-time-chip is-neutral">
                        <span className="etc-label">Kết thúc</span>
                        <span className="etc-time">{p.time}</span>
                        <span className="etc-date">{p.date}</span>
                      </span>
                    ); })()}
                    {ev.location && <span className="form-hint" style={{ margin: 0, fontSize: 12 }}>📍 {ev.location}</span>}
                    {(Array.isArray(ev.notifyBans) && ev.notifyBans.length) || (Array.isArray(ev.notifyPositions) && ev.notifyPositions.length) ? (
                      <span className="form-hint" style={{ margin: 0, fontSize: 12 }}>
                        👥 Chỉ hiện cho: {[...(ev.notifyBans || []).map(b => BAN_OPTS.find(o => o.key === b)?.label || b),
                             ...(ev.notifyPositions || []).map(p => POSITION_OPTS.find(o => o.key === p)?.label || p)].join(', ')}
                      </span>
                    ) : null}
                  </div>
                  {ev.description && (
                    <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: 13 }}>{ev.description}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => startEdit(ev)}>Sửa</button>
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteEvent(ev)}>Xoá</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
        </>
      )}
    </div>
  );
};

export default EventCalendar;
